import { create } from "zustand"

/**
 * Commission filter types for Submission/Manage pages
 */
export type SubmissionCommissionRange =
  | "HIGHEST"
  | "LOWEST"
  | "LESS_THAN_3"
  | "3_TO_5"
  | "6_TO_10"
  | "11_TO_14"
  | "MORE_THAN_15"
  | "NONE"

/**
 * Campaign type filter
 */
export type CampaignType = "ALL" | "HAS_CAST" | "NO_CAST" | null

/**
 * Status filter options
 */
export type SubmissionStatus =
  | "ALL"
  | "WAITING"
  | "BRAND_APPROVED"
  | "OVERDUE"
  | "FINISHED"
  | "REJECTED"
  | null

/**
 * Reminder/Error filter options
 */
export type ReminderFilter =
  | "NONE"
  | "NOT_ATTACH_LINK"
  | "NOT_AUTHORIZED_ORDER_SAMPLE"
  | "OVERDUE"
  | null

/**
 * Tag filter options
 */
export type TagFilter = "ALL" | "ZXU" | "SHOW_FACE" | null

/**
 * Filter state for Submission/Manage pages
 */
export interface SubmissionFilterValues {
  order?: "ASC" | "DESC"
  campaignType?: CampaignType
  status?: SubmissionStatus
  reminder?: ReminderFilter
  tag?: TagFilter
  commissionRange?: SubmissionCommissionRange
  isSelfFaceRequired?: boolean | null
}

interface SubmissionFilterStore {
  filter: SubmissionFilterValues | null

  // Actions
  setFilter: (filter: SubmissionFilterValues) => void
  reset: () => void
  calculateFilterCount: () => number
}

export const DEFAULT_FILTER_SUBMISSION: SubmissionFilterValues = {
  order: "DESC",
  campaignType: "ALL",
  status: "ALL",
  reminder: "NONE",
  tag: "ALL",
  commissionRange: "NONE",
  isSelfFaceRequired: null
}

const createDefaultFilter = (): SubmissionFilterValues => ({
  ...DEFAULT_FILTER_SUBMISSION
})

// Helper function to convert commission range to rate values
const getCommissionRangeValues = (
  commissionRange: SubmissionCommissionRange
): {
  startCommissionRate?: number
  endCommissionRate?: number
  sortBy?: string
  order?: "ASC" | "DESC"
} => {
  switch (commissionRange) {
    case "HIGHEST":
      return { sortBy: "commission", order: "DESC" }
    case "LOWEST":
      return { sortBy: "commission", order: "ASC" }
    case "LESS_THAN_3":
      return { startCommissionRate: 0, endCommissionRate: 3 }
    case "3_TO_5":
      return { startCommissionRate: 3, endCommissionRate: 5 }
    case "6_TO_10":
      return { startCommissionRate: 6, endCommissionRate: 10 }
    case "11_TO_14":
      return { startCommissionRate: 11, endCommissionRate: 14 }
    case "MORE_THAN_15":
      return { startCommissionRate: 15 }
    case "NONE":
    default:
      return {}
  }
}

// Helper function to convert tag filter to API params
const getTagParams = (tag: TagFilter): { tag?: string; isSelfFaceRequired?: boolean } => {
  if (!tag || tag === "ALL") return {}
  if (tag === "ZXU") return { tag: "ZXU" }
  if (tag === "SHOW_FACE") return { isSelfFaceRequired: true }
  return {}
}

// Helper function to convert campaign type to API params
const getCampaignTypeParams = (campaignType: CampaignType): { haveCast?: boolean } => {
  switch (campaignType) {
    case "HAS_CAST":
      return { haveCast: true }
    case "NO_CAST":
      return { haveCast: false }
    case "ALL":
    default:
      return {}
  }
}

// Transform filter state to API query params
export const transformFilterSubmissionToQueryParams = (
  filter: SubmissionFilterValues | null
): Record<string, any> => {
  if (!filter) return {}

  const params: Record<string, any> = {}

  // Handle order and sortBy
  if (filter.order) {
    params.order = filter.order
    params.sortBy = "updatedAt" // Default sort field for submissions
  }

  // Handle commission range (may override sortBy and order)
  if (filter.commissionRange && filter.commissionRange !== "NONE") {
    const commissionParams = getCommissionRangeValues(filter.commissionRange)
    Object.assign(params, commissionParams)
  }

  // Handle status
  if (filter.status && filter.status !== "ALL") {
    params.status = filter.status
  }

  // Handle campaign type
  if (filter.campaignType && filter.campaignType !== "ALL") {
    Object.assign(params, getCampaignTypeParams(filter.campaignType))
  }

  // Handle tag
  if (filter.tag && filter.tag !== "ALL") {
    Object.assign(params, getTagParams(filter.tag))
  }

  // Handle isSelfFaceRequired
  if (filter.isSelfFaceRequired !== null && filter.isSelfFaceRequired !== undefined) {
    params.isSelfFaceRequired = filter.isSelfFaceRequired
  }

  return params
}

const calculateFilterCount = (filter: SubmissionFilterValues | null): number => {
  if (!filter) return 0

  let count = 0

  // Count order only if not default DESC
  if (filter.order && filter.order !== "DESC") count++

  // Count campaign type only if not ALL
  if (filter.campaignType && filter.campaignType !== "ALL") count++

  // Count status only if not ALL
  if (filter.status && filter.status !== "ALL") count++

  // Count reminder only if not NONE
  if (filter.reminder && filter.reminder !== "NONE") count++

  // Count tag only if not ALL
  if (filter.tag && filter.tag !== "ALL") count++

  // Count commission range only if not NONE
  if (filter.commissionRange && filter.commissionRange !== "NONE") count++

  // Count isSelfFaceRequired if explicitly set
  if (filter.isSelfFaceRequired !== null && filter.isSelfFaceRequired !== undefined) count++

  return count
}

export const useFilterSubmission = create<SubmissionFilterStore>()((set, get) => ({
  filter: createDefaultFilter(),

  setFilter: (filter) => {
    set({ filter: { ...filter } })
  },

  reset: () => {
    set({ filter: createDefaultFilter() })
  },

  calculateFilterCount: () => {
    const state = get()
    return calculateFilterCount(state.filter)
  }
}))
